import { SectionHeading, SectionLabel } from "./Section";

const scans = [
    {
        key: "invoice",
        title: "Invoice Scan",
        detail: "Vendor · GST · Due date",
        art: <InvoiceArt />,
        span: "lg:col-span-2",
    },
    {
        key: "excel",
        title: "Excel Scan",
        detail: "Rows → structured insight",
        art: <ExcelArt />,
        span: "",
    },
    {
        key: "warehouse",
        title: "Warehouse Scan",
        detail: "Stock, aging, shrinkage",
        img: "/scan-warehouse.jpg",
        span: "",
    },
    {
        key: "dashboard",
        title: "Dashboard Scan",
        detail: "Screens → decisions",
        img: "/scan-dashboard.jpg",
        span: "",
    },
    {
        key: "whiteboard",
        title: "Whiteboard Scan",
        detail: "Meetings → action cards",
        img: "/scan-whiteboard.jpg",
        span: "",
    },
];

function InvoiceArt() {
    return (
        <div className="relative h-full min-h-[220px] rounded-2xl border border-black/5 bg-white overflow-hidden">
            <div className="absolute inset-x-6 top-6 h-3 rounded-full bg-neutral-100" />
            <div className="absolute inset-x-6 top-12 h-2 w-3/4 rounded-full bg-neutral-100" />
            <div className="absolute inset-x-6 top-20 grid grid-cols-3 gap-3">
                {[0, 1, 2].map((i) => (
                    <div key={i} className="h-14 rounded-xl bg-neutral-50 border border-black/5" />
                ))}
            </div>
            <div className="absolute inset-x-6 bottom-6 h-16 rounded-2xl border border-violet-200 bg-violet-50/60 flex items-center justify-between px-4">
                <div className="text-[11px] font-semibold tracking-[0.22em] uppercase text-violet-600">
                    Insight → Task
                </div>
                <div className="text-[13px] font-semibold text-black">
                    ₹1,42,000 due in 3d
                </div>
            </div>
            <ScanLine />
        </div>
    );
}

function ExcelArt() {
    return (
        <div className="relative h-full min-h-[220px] rounded-2xl border border-black/5 bg-white overflow-hidden p-3">
            <div className="grid grid-cols-4 gap-1.5 text-[10px]">
                {Array.from({ length: 24 }).map((_, i) => (
                    <div
                        key={i}
                        className={`h-6 rounded-md ${
                            i % 4 === 0
                                ? "bg-neutral-100"
                                : i === 7 || i === 15
                                    ? "bg-violet-100"
                                    : "bg-neutral-50 border border-black/[0.03]"
                        }`}
                    />
                ))}
            </div>
            <ScanLine />
        </div>
    );
}

function ScanLine() {
    return (
        <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-violet-500 to-transparent anim-scan" />
    );
}

export default function ScanAI() {
    return (
        <section id="scan-ai" data-testid="scan-section" className="relative py-28 sm:py-36">
            <div className="mx-auto max-w-7xl px-6 lg:px-8">
                <div className="reveal max-w-3xl">
                    <SectionLabel num="07">Business Scan AI</SectionLabel>
                    <SectionHeading className="mt-6">
                        Scan anything.{" "}
                        <span className="italic font-normal">
                            Get decisions instantly.
                        </span>
                    </SectionHeading>
                    <p className="mt-6 text-[15px] sm:text-lg text-neutral-600 leading-relaxed max-w-2xl">
                        Every scan produces Insights, Risks, Tasks and CTA
                        Cards — ready for one-tap action inside the command
                        center.
                    </p>
                </div>

                <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {scans.map((s, i) => (
                        <div
                            key={s.key}
                            data-testid={`scan-${s.key}`}
                            className={`reveal glass rounded-3xl p-5 hover:-translate-y-1 hover:shadow-[0_20px_60px_-20px_rgba(139,92,246,0.3)] transition-all duration-500 flex flex-col ${s.span}`}
                            style={{ transitionDelay: `${i * 80}ms` }}
                        >
                            <div className="flex items-center justify-between mb-4">
                                <span className="inline-flex items-center gap-1.5 text-[10px] font-semibold tracking-[0.2em] uppercase text-violet-600 bg-violet-50 rounded-full px-2.5 py-1">
                                    Scan
                                </span>
                                <span className="font-mono-alt text-[10px] text-neutral-400">
                                    /0{i + 1}
                                </span>
                            </div>

                            {s.img ? (
                                <div className="relative rounded-2xl overflow-hidden border border-black/5 h-[220px] bg-neutral-50">
                                    <img
                                        src={s.img}
                                        alt={s.title}
                                        className="absolute inset-0 h-full w-full object-cover"
                                        loading="lazy"
                                    />
                                    <div className="absolute inset-0 bg-white/30 mix-blend-lighten" />
                                    <ScanLine />
                                    <div className="absolute inset-x-3 bottom-3 rounded-xl bg-white/85 backdrop-blur-md border border-white/60 px-3 py-2">
                                        <div className="text-[10px] font-semibold tracking-[0.2em] uppercase text-violet-600">
                                            Insight
                                        </div>
                                        <div className="text-[12px] text-neutral-800">
                                            Converted to a CEO decision card
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                s.art
                            )}

                            <div className="mt-5">
                                <div className="font-display text-[19px] font-medium tracking-tight text-black">
                                    {s.title}
                                </div>
                                <div className="text-[12.5px] text-neutral-500 mt-1">
                                    {s.detail}
                                </div>
                            </div>

                            <div className="mt-4 flex flex-wrap gap-1.5">
                                {["Insight", "Risk", "Task", "CTA"].map((t) => (
                                    <span key={t} className="rounded-full bg-white border border-black/5 text-[10px] font-medium text-neutral-600 px-2 py-1">
                                        {t}
                                    </span>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}
